import { Backdrop, CircularProgress, Container, Grid, Card, CardHeader, CardContent, Typography, Button, Box } from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import SpeedIcon from '@mui/icons-material/Speed';
import Breadcrumbs from "components/ppa-quote/Breadcrumbs";
import UserContext from "Context/UserProvider";
import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useApiService } from "../../services/ApiService/ApiService";

interface QuoteSummary {
    id: string,
    planNumberOfVehicles: number,
    planAverageDistancePerDay: number,
    planAverageChargesPerMonth: number,
    planAverageChargeSizeKWh: number,
    planMonthlyChargingRequirementKWh: number,
    connectedNumberOfVehicles: number,
    connectedAverageDistancePerDay: number,
    connectedAverageChargesPerMonth: number,
    connectedAverageChargeSizeKWh: number,
    connectedMonthlyChargingRequirementKWh: number,
    quickQuoteNumberOfVehicles: number,
    quickQuoteAverageChargesPerMonth: number,
    quickQuoteAverageChargeSizeKWh: number,
    quickQuoteMonthlyChargingRequirementKWh: number,
}

// Helper component for summary items
const SummaryItem = ({ title, value }: { title: string, value: string }) => (
    <Box sx={{ py: 1.5, display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid', borderColor: 'grey.200' }}>
        <Typography variant="body2" color="text.secondary">{title}</Typography>
        <Typography variant="body1" fontWeight="medium">{value}</Typography>
    </Box>
);

function BuildQuotePageSummary(props: any) {
    const [loading, setLoading] = useState<boolean>(true);
    const { userInfo } = useContext(UserContext);
    const [isDisabled, setIsDisabled] = useState<boolean>(false);
    const [quoteSummary, setQuoteSummary] = useState<QuoteSummary | null>(null);
    const navigate = useNavigate();
    const [fleetScenarioUuidList, setFleetScenarioUuidList] = useState<string[]>([]);
    const [electricVehicleFleetUuidList, setElectricVehicleFleetUuidList] = useState<string[]>([]);
    const location = useLocation();
    const quickQuote = location.state.quickQuote;
    const { fetchWithAuth } = useApiService();

    const quoteSummaryMapping = (e: QuoteSummary): QuoteSummary & { id: string } => ({
        ...e,
        planNumberOfVehicles: e.planNumberOfVehicles,
        planAverageDistancePerDay: e.planAverageDistancePerDay,
        planAverageChargesPerMonth: e.planAverageChargesPerMonth,
        planAverageChargeSizeKWh: e.planAverageChargeSizeKWh,
        planMonthlyChargingRequirementKWh: e.planMonthlyChargingRequirementKWh,
        connectedNumberOfVehicles: e.connectedNumberOfVehicles,
        connectedAverageDistancePerDay: e.connectedAverageDistancePerDay,
        connectedAverageChargesPerMonth: e.connectedAverageChargesPerMonth,
        connectedAverageChargeSizeKWh: e.connectedAverageChargeSizeKWh,
        connectedMonthlyChargingRequirementKWh: e.connectedMonthlyChargingRequirementKWh,
        quickQuoteNumberOfVehicles: e.quickQuoteNumberOfVehicles,
        quickQuoteAverageChargesPerMonth: e.quickQuoteAverageChargesPerMonth,
        quickQuoteAverageChargeSizeKWh: e.quickQuoteAverageChargeSizeKWh,
        quickQuoteMonthlyChargingRequirementKWh: e.quickQuoteMonthlyChargingRequirementKWh
    });

    const fetchSummary = async () => {
        try {
            const response = await fetchWithAuth('/ppa/advanced/build/summary/get', {
                method: 'POST',
                body: JSON.stringify({
                    uuid: userInfo.uuid,
                    fleetUuid: userInfo.fleetUuid,
                    quickQuote: quickQuote
                })
            });

            if (!response.ok) {
                throw new Error('Failed to fetch summary');
            }

            const res_json = await response.json();
            setQuoteSummary(quoteSummaryMapping(res_json));
            setFleetScenarioUuidList(res_json.fleetScenarioUuidList);
            setElectricVehicleFleetUuidList(res_json.electricVehicleFleetUuidList);
        } catch (error) {
            console.error('Error fetching summary:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSummary();
    }, []);

    const handleNext = () => {
        console.log(JSON.stringify(quickQuote))
        navigate("/agree/ppa/quote/build/results", {
            state: {
                fleetScenarioUuidList: fleetScenarioUuidList,
                electricVehicleFleetUuidList: electricVehicleFleetUuidList,
                quickQuote: quickQuote,
            },
        })
    };

    return (
        <div>
            <Breadcrumbs currentStep={"Summary"} />

            <Backdrop open={loading} sx={{ zIndex: 1300 }}>
                <CircularProgress color="inherit" />
            </Backdrop>

            {!loading && quoteSummary && (
                <Container maxWidth="lg" sx={{ mt: 4 }}>
                    <Grid container spacing={3}>
                        {/* Plan Based Vehicles */}
                        <Grid item xs={12} md={4}>
                            <Card elevation={2} sx={{ height: '100%' }}>
                                <CardHeader
                                    avatar={<AssessmentIcon color="primary" />}
                                    title="Plan Based Vehicles"
                                    sx={{
                                        backgroundColor: (theme) => theme.palette.grey[50],
                                        borderBottom: (theme) => `1px solid ${theme.palette.divider}`
                                    }}
                                />
                                <CardContent>
                                    <SummaryItem 
                                        title="Number of vehicles"
                                        value={quoteSummary.planNumberOfVehicles?.toFixed(0)}
                                    />
                                    <SummaryItem 
                                        title="Average distance per day"
                                        value={`${quoteSummary.planAverageDistancePerDay?.toFixed(2)} km`}
                                    />
                                    <SummaryItem 
                                        title="Average charges per month"
                                        value={quoteSummary.planAverageChargesPerMonth?.toFixed(0)}
                                    />
                                    <SummaryItem 
                                        title="Average size of charge"
                                        value={`${quoteSummary.planAverageChargeSizeKWh?.toFixed(2)} kW/h`}
                                    />
                                    <SummaryItem 
                                        title="Monthly Charging Requirement"
                                        value={`${quoteSummary.planMonthlyChargingRequirementKWh?.toFixed(2)} kW/h`}
                                    />
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Connected Vehicles */}
                        <Grid item xs={12} md={4}>
                            <Card elevation={2} sx={{ height: '100%' }}>
                                <CardHeader
                                    avatar={<DirectionsCarIcon color="primary" />}
                                    title="Connected Vehicles"
                                    sx={{
                                        backgroundColor: (theme) => theme.palette.grey[50],
                                        borderBottom: (theme) => `1px solid ${theme.palette.divider}`
                                    }}
                                />
                                <CardContent>
                                    <SummaryItem 
                                        title="Number of vehicles"
                                        value={quoteSummary.connectedNumberOfVehicles?.toFixed(0)}
                                    />
                                    <SummaryItem 
                                        title="Average distance per day"
                                        value={`${quoteSummary.connectedAverageDistancePerDay?.toFixed(2)} km`}
                                    />
                                    <SummaryItem 
                                        title="Average charges per month"
                                        value={quoteSummary.connectedAverageChargesPerMonth?.toFixed(0)}
                                    />
                                    <SummaryItem 
                                        title="Average size of charge"
                                        value={`${quoteSummary.connectedAverageChargeSizeKWh?.toFixed(2)} kW/h`}
                                    />
                                    <SummaryItem 
                                        title="Monthly Charging Requirement"
                                        value={`${quoteSummary.connectedMonthlyChargingRequirementKWh?.toFixed(2)} kW/h`}
                                    />
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Quick Quote */}
                        <Grid item xs={12} md={4}>
                            <Card elevation={2} sx={{ height: '100%' }}>
                                <CardHeader
                                    avatar={<SpeedIcon color="primary" />}
                                    title="Quick Quote"
                                    sx={{
                                        backgroundColor: (theme) => theme.palette.grey[50],
                                        borderBottom: (theme) => `1px solid ${theme.palette.divider}`
                                    }}
                                />
                                <CardContent>
                                    <SummaryItem 
                                        title="Number of vehicles"
                                        value={quoteSummary.quickQuoteNumberOfVehicles?.toFixed(0)}
                                    />
                                    <SummaryItem 
                                        title="Average charges per month"
                                        value={quoteSummary.quickQuoteAverageChargesPerMonth?.toFixed(0)}
                                    />
                                    <SummaryItem 
                                        title="Average size of charge"
                                        value={quoteSummary.quickQuoteAverageChargeSizeKWh === undefined ? 
                                            "0.00 kW/h" : 
                                            `${quoteSummary.quickQuoteAverageChargeSizeKWh?.toFixed(2)} kW/h`}
                                    />
                                    <SummaryItem 
                                        title="Monthly Charging Requirement"
                                        value={quoteSummary.quickQuoteMonthlyChargingRequirementKWh === undefined ? 
                                            "0.00 kW/h" : 
                                            `${quoteSummary.quickQuoteMonthlyChargingRequirementKWh?.toFixed(2)} kW/h`}
                                    />
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 4 }}>
                        <Button
                            variant="contained"
                            color="primary"
                            disabled={isDisabled}
                            onClick={handleNext}
                            sx={{ minWidth: 200 }}
                        >
                            Get Quotes
                        </Button>
                    </Box>
                </Container>
            )}
        </div>
    );
}

export default BuildQuotePageSummary;
